import React, { useState } from 'react';
import { orderAPI } from '../services/api';

const PaymentConfirmationModal = ({
    isOpen,
    onClose,
    bookingDetails,
    onPaymentSuccess
}) => {
    const [loading, setLoading] = useState(false);
    const [customerInfo, setCustomerInfo] = useState({
        name: '',
        email: '',
        whatsapp: ''
    });
    const [errors, setErrors] = useState({});

    // Reset form when modal is closed
    React.useEffect(() => {
        if (!isOpen) {
            setCustomerInfo({
                name: '',
                email: '',
                whatsapp: ''
            });
            setErrors({});
            setLoading(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const { selectedDate, selectedTime, service } = bookingDetails;

    // Get price as number (remove ₹ and convert)
    const getPrice = () => {
        if (service.price === 'Price varies') return 0;
        return parseInt(service.price.replace('₹', '').replace(',', ''));
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        if (!customerInfo.name.trim()) {
            newErrors.name = 'Name is required';
        }

        if (!customerInfo.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerInfo.email)) {
            newErrors.email = 'Enter a valid email address';
        }

        if (!customerInfo.whatsapp.trim()) {
            newErrors.whatsapp = 'WhatsApp number is required';
        } else if (!/^[6-9]\d{9}$/.test(customerInfo.whatsapp.replace(/\D/g, ''))) {
            newErrors.whatsapp = 'Enter a valid 10-digit WhatsApp number';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCustomerInfo(prev => ({ ...prev, [name]: value }));
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // PhonePe uses redirect flow, no script loading needed

    // Handle payment
    const handlePayment = async () => {
        if (!validateForm()) return;

        setLoading(true);

        try {
            // Create order
            const orderData = {
                amount: getPrice(),
                customerName: customerInfo.name,
                customerEmail: customerInfo.email,
                customerWhatsapp: customerInfo.whatsapp,
                serviceType: service.name,
                appointmentDate: `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`,
                appointmentTime: selectedTime
            };

            // Detailed date debugging
            console.log('Date debugging:', {
                selectedDate: selectedDate,
                selectedDateString: selectedDate.toString(),
                selectedDateISO: selectedDate.toISOString(),
                year: selectedDate.getFullYear(),
                month: selectedDate.getMonth() + 1,
                day: selectedDate.getDate(),
                hours: selectedDate.getHours(),
                appointmentDate: orderData.appointmentDate
            });

            // console.log('Creating order with data:', orderData);

            const { transactionId, bookingId, redirectUrl } = await orderAPI.createOrder(orderData);

            if (!redirectUrl) {
                throw new Error('Failed to get payment redirect URL');
            }

            // Store booking details in sessionStorage for use after redirect
            sessionStorage.setItem('pendingBooking', JSON.stringify({
                bookingId: bookingId,
                transactionId: transactionId,
                customerInfo,
                service,
                selectedDate: selectedDate.toISOString(),
                selectedTime
            }));

            // Redirect to PhonePe payment page
            window.location.href = redirectUrl;

        } catch (error) {
            console.error('Error initiating payment:', error);
            alert(error.message || 'Failed to initiate payment. Please try again.');
            setLoading(false);
        }
    };

    // Format date for display
    const formatDate = () => {
        const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
        return selectedDate.toLocaleDateString('en-US', options);
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-300">
            {/* Modal container with flex column layout to ensure footer is always visible */}
            <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] sm:max-h-[95vh] flex flex-col overflow-hidden shadow-2xl animate-in slide-in-from-bottom-8 duration-500">
                {/* Header with gradient - flex-shrink-0 ensures it never shrinks */}
                <div className="flex-shrink-0 relative bg-gradient-to-r from-gray-50 to-gray-100 px-4 sm:px-6 lg:px-8 py-4 sm:py-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="font-cormorant font-bold text-xl sm:text-2xl lg:text-4xl text-gray-900 mb-1">
                                Confirm Your Booking
                            </h2>
                            <p className="font-nunito text-xs sm:text-sm text-gray-600">
                                Review your details and proceed to secure payment
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            disabled={loading}
                            className="p-2 hover:bg-white/80 rounded-full transition-all duration-200 disabled:opacity-50 group flex-shrink-0"
                        >
                            <svg className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600 group-hover:text-gray-800 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Content - flex-1 takes remaining space, overflow-y-auto allows scrolling */}
                <div className="flex-1 overflow-y-auto min-h-0">
                    <div className="p-4 sm:p-6 lg:p-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
                            {/* Left Column - Service Details */}
                            <div className="space-y-4 sm:space-y-6">
                                <div>
                                    <h3 className="font-nunito font-bold text-base sm:text-lg text-gray-900 mb-3 sm:mb-4 flex items-center">
                                        <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-black rounded-full mr-2 sm:mr-3"></div>
                                        Service Details
                                    </h3>

                                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-200">
                                        <div className="mb-3 sm:mb-4">
                                            <h4 className="font-cormorant font-bold text-lg sm:text-xl lg:text-2xl text-gray-900 mb-1 sm:mb-2">
                                                {service.name}
                                            </h4>
                                            <p className="font-nunito text-xs sm:text-sm text-gray-600 leading-relaxed line-clamp-2 sm:line-clamp-none">
                                                {service.description}
                                            </p>
                                        </div>

                                        <div className="space-y-2 sm:space-y-3 pt-3 sm:pt-4 border-t border-gray-300">
                                            <div className="flex items-center justify-between">
                                                <span className="font-nunito text-xs sm:text-sm font-medium text-gray-700">Date</span>
                                                <span className="font-nunito text-xs sm:text-sm font-bold text-gray-900">{formatDate()}</span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="font-nunito text-xs sm:text-sm font-medium text-gray-700">Time</span>
                                                <span className="font-nunito text-xs sm:text-sm font-bold text-gray-900">{selectedTime}</span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="font-nunito text-xs sm:text-sm font-medium text-gray-700">Duration</span>
                                                <span className="font-nunito text-xs sm:text-sm font-bold text-gray-900">{service.duration}</span>
                                            </div>
                                            <div className="flex items-center justify-between pt-2 border-t border-gray-300">
                                                <span className="font-nunito text-sm sm:text-base font-bold text-gray-900">Total</span>
                                                <span className="font-cormorant text-xl sm:text-2xl font-bold text-black">{service.price}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Right Column - Customer Information */}
                            <div className="space-y-4 sm:space-y-6">
                                <div>
                                    <h3 className="font-nunito font-bold text-base sm:text-lg text-gray-900 mb-3 sm:mb-4 flex items-center">
                                        <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-black rounded-full mr-2 sm:mr-3"></div>
                                        Your Information
                                    </h3>

                                    <div className="space-y-3 sm:space-y-5">
                                        <div className="group">
                                            <label className="block font-nunito text-xs sm:text-sm font-semibold text-gray-800 mb-1.5 sm:mb-2">
                                                Full Name *
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    name="name"
                                                    value={customerInfo.name}
                                                    onChange={handleInputChange}
                                                    className={`w-full px-3 sm:px-4 py-2.5 sm:py-3.5 border-2 rounded-lg sm:rounded-xl font-nunito text-sm bg-white focus:outline-none transition-all duration-200 ${errors.name
                                                        ? 'border-red-400 focus:border-red-500 focus:ring-4 focus:ring-red-100'
                                                        : 'border-gray-200 focus:border-black focus:ring-4 focus:ring-gray-100 hover:border-gray-300'
                                                        }`}
                                                    placeholder="Enter your full name"
                                                />
                                                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                    </svg>
                                                </div>
                                            </div>
                                            {errors.name && (
                                                <p className="mt-1.5 sm:mt-2 text-xs sm:text-sm text-red-600 font-nunito flex items-center">
                                                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                    </svg>
                                                    {errors.name}
                                                </p>
                                            )}
                                        </div>

                                        <div className="group">
                                            <label className="block font-nunito text-xs sm:text-sm font-semibold text-gray-800 mb-1.5 sm:mb-2">
                                                Email Address *
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={customerInfo.email}
                                                    onChange={handleInputChange}
                                                    className={`w-full px-3 sm:px-4 py-2.5 sm:py-3.5 border-2 rounded-lg sm:rounded-xl font-nunito text-sm bg-white focus:outline-none transition-all duration-200 ${errors.email
                                                        ? 'border-red-400 focus:border-red-500 focus:ring-4 focus:ring-red-100'
                                                        : 'border-gray-200 focus:border-black focus:ring-4 focus:ring-gray-100 hover:border-gray-300'
                                                        }`}
                                                    placeholder="Enter your email"
                                                />
                                                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                    </svg>
                                                </div>
                                            </div>
                                            {errors.email && (
                                                <p className="mt-1.5 sm:mt-2 text-xs sm:text-sm text-red-600 font-nunito flex items-center">
                                                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                    </svg>
                                                    {errors.email}
                                                </p>
                                            )}
                                        </div>

                                        <div className="group">
                                            <label className="block font-nunito text-xs sm:text-sm font-semibold text-gray-800 mb-1.5 sm:mb-2">
                                                WhatsApp Number *
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type="tel"
                                                    name="whatsapp"
                                                    value={customerInfo.whatsapp}
                                                    onChange={handleInputChange}
                                                    className={`w-full px-3 sm:px-4 py-2.5 sm:py-3.5 border-2 rounded-lg sm:rounded-xl font-nunito text-sm bg-white focus:outline-none transition-all duration-200 ${errors.whatsapp
                                                        ? 'border-red-400 focus:border-red-500 focus:ring-4 focus:ring-red-100'
                                                        : 'border-gray-200 focus:border-black focus:ring-4 focus:ring-gray-100 hover:border-gray-300'
                                                        }`}
                                                    placeholder="9999999999"
                                                />
                                                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                                    </svg>
                                                </div>
                                            </div>
                                            {errors.whatsapp && (
                                                <p className="mt-1.5 sm:mt-2 text-xs sm:text-sm text-red-600 font-nunito flex items-center">
                                                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                    </svg>
                                                    {errors.whatsapp}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer - flex-shrink-0 ensures it's ALWAYS visible */}
                <div className="flex-shrink-0 bg-gray-50 px-4 sm:px-6 lg:px-8 py-4 sm:py-6 border-t border-gray-200">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
                        <button
                            onClick={onClose}
                            disabled={loading}
                            className="w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-3 border-2 border-gray-300 rounded-xl font-nunito font-semibold text-sm text-gray-700 hover:bg-white hover:border-gray-400 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed order-2 sm:order-1"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handlePayment}
                            disabled={loading || service.price === 'Price varies'}
                            className="w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-3 bg-black text-white rounded-xl font-nunito font-bold text-sm hover:bg-gray-900 transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 sm:gap-3 shadow-lg hover:shadow-xl order-1 sm:order-2"
                        >
                            {loading ? (
                                <>
                                    <svg className="animate-spin h-4 w-4 sm:h-5 sm:w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span className="text-xs sm:text-sm">Processing...</span>
                                </>
                            ) : (
                                <>
                                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                    <span className="text-xs sm:text-sm">Secure Payment {service.price !== 'Price varies' && `• ${service.price}`}</span>
                                </>
                            )}
                        </button>
                    </div>
                    {service.price === 'Price varies' && (
                        <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-blue-50 rounded-xl border border-blue-200">
                            <p className="text-xs sm:text-sm text-blue-800 font-nunito text-center">
                                <svg className="w-4 h-4 inline mr-1 sm:mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                                We'll contact you with pricing details
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PaymentConfirmationModal; 